// ============================================================================
// STOCK AVAILABILITY CHECK COMPONENT
// Reusable component for displaying stock availability status and warnings
// Date: June 2026
// ============================================================================

'use client';

import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Package,
  Warehouse,
  TrendingUp,
  Clock,
  Info,
} from 'lucide-react';
import type { StockAvailabilityCheck as StockAvailabilityCheckType } from '@/lib/inventory/stockReservationService';

interface StockAvailabilityCheckProps {
  availability: StockAvailabilityCheckType;
  showDetails?: boolean;
  compact?: boolean;
  isDark?: boolean;
}

export function StockAvailabilityCheck({
  availability,
  showDetails = true,
  compact = false,
  isDark = false,
}: StockAvailabilityCheckProps) {
  const {
    quantity_on_hand,
    quantity_reserved,
    quantity_available,
    quantity_incoming,
    requested_quantity,
    can_fulfill,
    shortage,
    backorder_required,
  } = availability;

  const getStatusColor = () => {
    if (can_fulfill) {
      return isDark ? 'text-emerald-400' : 'text-emerald-600';
    } else if (backorder_required) {
      return isDark ? 'text-amber-400' : 'text-amber-600';
    } else {
      return isDark ? 'text-red-400' : 'text-red-600';
    }
  };

  const getStatusBgColor = () => {
    if (can_fulfill) {
      return isDark ? 'bg-emerald-950/30 border-emerald-800' : 'bg-emerald-50 border-emerald-200';
    } else if (backorder_required) {
      return isDark ? 'bg-amber-950/30 border-amber-800' : 'bg-amber-50 border-amber-200';
    } else {
      return isDark ? 'bg-red-950/30 border-red-800' : 'bg-red-50 border-red-200';
    }
  };

  const getStatusIcon = () => {
    if (can_fulfill) {
      return <CheckCircle2 size={16} className={getStatusColor()} />;
    } else if (backorder_required) {
      return <AlertTriangle size={16} className={getStatusColor()} />;
    } else {
      return <XCircle size={16} className={getStatusColor()} />;
    }
  };

  const getStatusText = () => {
    if (can_fulfill) {
      return 'In Stock';
    } else if (backorder_required) {
      return 'Backorder Required';
    } else {
      return 'Out of Stock';
    }
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${getStatusBgColor()}`}>
        {getStatusIcon()}
        <span className={`text-sm font-medium ${getStatusColor()}`}>
          {quantity_available} available
        </span>
        {!can_fulfill && (
          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            (need {shortage})
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-lg border ${getStatusBgColor()}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className={`font-semibold ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Requested: {requested_quantity}
        </div>
      </div>

      {showDetails && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Package size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                On Hand: {quantity_on_hand}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Warehouse size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                Reserved: {quantity_reserved}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                Available: {quantity_available}
              </span>
            </div>
            {quantity_incoming > 0 && (
              <div className="flex items-center gap-2">
                <TrendingUp size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                  Incoming: {quantity_incoming}
                </span>
              </div>
            )}
          </div>

          {!can_fulfill && (
            <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-start gap-2">
                <Info size={14} className={isDark ? 'text-amber-400' : 'text-amber-600 mt-0.5'} />
                <div className="text-sm">
                  {backorder_required ? (
                    <span className={isDark ? 'text-amber-300' : 'text-amber-700'}>
                      Shortage of {shortage} units. This will be placed on backorder.
                    </span>
                  ) : (
                    <span className={isDark ? 'text-red-300' : 'text-red-700'}>
                      Insufficient stock. Cannot fulfill order.
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface BulkStockAvailabilityCheckProps {
  availabilities: StockAvailabilityCheckType[];
  showDetails?: boolean;
  isDark?: boolean;
  onItemSelect?: (work_item_id: string) => void;
}

export function BulkStockAvailabilityCheck({
  availabilities,
  showDetails = true,
  isDark = false,
  onItemSelect,
}: BulkStockAvailabilityCheckProps) {
  const totalRequested = availabilities.reduce((sum, a) => sum + a.requested_quantity, 0);
  const totalAvailable = availabilities.reduce((sum, a) => sum + a.quantity_available, 0);
  const totalShortage = availabilities.reduce((sum, a) => sum + a.shortage, 0);
  const allCanFulfill = availabilities.every(a => a.can_fulfill);
  const anyBackorder = availabilities.some(a => a.backorder_required);

  const getOverallStatus = () => {
    if (allCanFulfill) {
      return {
        color: isDark ? 'text-emerald-400' : 'text-emerald-600',
        bgColor: isDark ? 'bg-emerald-950/30 border-emerald-800' : 'bg-emerald-50 border-emerald-200',
        icon: <CheckCircle2 size={20} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />,
        text: 'All Items Available',
      };
    } else if (anyBackorder) {
      return {
        color: isDark ? 'text-amber-400' : 'text-amber-600',
        bgColor: isDark ? 'bg-amber-950/30 border-amber-800' : 'bg-amber-50 border-amber-200',
        icon: <AlertTriangle size={20} className={isDark ? 'text-amber-400' : 'text-amber-600'} />,
        text: 'Partial Availability - Backorder Required',
      };
    } else {
      return {
        color: isDark ? 'text-red-400' : 'text-red-600',
        bgColor: isDark ? 'bg-red-950/30 border-red-800' : 'bg-red-50 border-red-200',
        icon: <XCircle size={20} className={isDark ? 'text-red-400' : 'text-red-600'} />,
        text: 'Items Out of Stock',
      };
    }
  };

  const overallStatus = getOverallStatus();

  return (
    <div className={`p-4 rounded-lg border ${overallStatus.bgColor}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {overallStatus.icon}
          <div>
            <span className={`font-semibold ${overallStatus.color}`}>
              {overallStatus.text}
            </span>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {availabilities.length} items • {totalAvailable} of {totalRequested} units available
            </div>
          </div>
        </div>
        {totalShortage > 0 && (
          <div className={`text-sm font-medium ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
            Shortage: {totalShortage} units
          </div>
        )}
      </div>

      {showDetails && (
        <div className="space-y-2 mt-4">
          {availabilities.map((availability, index) => (
            <div
              key={`${availability.work_item_id}-${availability.warehouse_id}-${index}`}
              className={`p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'} ${onItemSelect ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50' : ''}`}
              onClick={() => onItemSelect?.(availability.work_item_id)}
            >
              <StockAvailabilityCheck
                availability={availability}
                showDetails={false}
                compact={true}
                isDark={isDark}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}